import { gql, Resolver } from '@apollo/client';

import {
    sortByLatestDate,
    getAllListPages,
    getTokenCallback,
} from '@trqk/utils';

/**
 * @typedef {GetActiveEntitiesArgs}
 * @property {string} clientId
 * @property {string} userId
 * @property {string} trqkDirectoryUrl URL for the TrqkDirectory GraphQL API
 */
interface GetActiveEntitiesArgs {
    clientId: string;
    userPoolId: string;
    trqkDirectoryUrl: string;
}
interface GetActiveEntitiesResult {
    activeEntities: ActiveEntity[];
}

/**
 * Get a list containing activeEntity items. These activeEntities will be used within the
 * useEntities hook to provide the list of entities used within the EntitySelector.
 * @param obj
 * @param {GetActiveEntitiesArgs} args
 * @param context
 * @returns {Promise<ActiveEntity[]>} an array of ActiveEntity items. Each one has an `entity` prop
 * as well as an `entityId` and a list of `completedStatementViews`, each of which have
 * statementView.status_name === 'COMPLETE'.
 */
export const getActiveEntities: Resolver = async (
    obj: Record<string, unknown>,
    args: GetActiveEntitiesArgs,
): Promise<GetActiveEntitiesResult> => {
    const { clientId, trqkDirectoryUrl, userPoolId } = args;

    // We can't use the getToken method provided by useTokenModal because the EntityProvider sits
    // above the ModalProvider in the component tree.
    const getToken = getTokenCallback({ clientId, userPoolId });

    const items = await getAllListPages<
        TrqkEntity | null,
        TableTrqkEntityFilterInput
    >({
        getToken,
        query: `
            query listTrqkEntitiesActive($filter: TableTrqkEntityFilterInput, $limit: Int, $nextToken: String) {
                listTrqkEntities(filter: $filter, limit: $limit, nextToken: $nextToken) {
                    items {
                        id
                        name
                        pro
                        role_type
                        ROW_LOCK
                    }
                    nextToken
                }
            }
        `,
        queryName: 'listTrqkEntities',
        trqkDirectoryUrl,
    });

    function itemIsNotNull(x: TrqkEntity | null): x is TrqkEntity {
        return x !== null;
    }

    const entities = items.filter(itemIsNotNull);

    const allEntities = await Promise.all(
        entities.map(
            (entity: TrqkEntity): Promise<ActiveEntity> =>
                new Promise((resolve, reject) => {
                    getAllListPages<
                        TrqkStatementView,
                        TableTrqkStatementViewFilterInput
                    >({
                        filter: {
                            entity_id: {
                                eq: entity.id,
                            },
                            status_name: {
                                eq: 'COMPLETE',
                            },
                        },
                        getToken,
                        query: `
                            query listTrqkStatementViewsActive($filter: TableTrqkStatementViewFilterInput, $limit: Int, $nextToken: String) {
                                listTrqkStatementViews(filter: $filter, limit: $limit, nextToken: $nextToken) {
                                    items {
                                        statement_id
                                        start_date
                                        status_name
                                        end_date
                                        pro
                                        region
                                        user_id
                                        unit
                                        earnings
                                        name
                                        role_type
                                    }
                                    nextToken
                                }
                            }
                        `,
                        queryName: 'listTrqkStatementViews',
                        trqkDirectoryUrl,
                    })
                        .then(
                            (
                                completedStatementViews: TrqkStatementView[],
                            ): void => {
                                completedStatementViews.sort(
                                    (
                                        viewA: TrqkStatementView,
                                        viewB: TrqkStatementView,
                                    ): number =>
                                        sortByLatestDate(
                                            viewA?.start_date ?? null,
                                            viewB?.start_date ?? null,
                                        ),
                                );

                                resolve({
                                    id: entity.id,
                                    entity,
                                    completedStatementViews,
                                });
                            },
                        )
                        .catch((error: Error): void => reject(error));
                }),
        ),
    );

    const activeEntities = allEntities.filter(
        ({ completedStatementViews }: ActiveEntity): boolean =>
            completedStatementViews.length > 0,
    );

    return {
        activeEntities,
    };
};

export const GET_ACTIVE_ENTITIES = gql`
    query GetActiveEntities(
        $clientId: String!
        $trqkDirectoryUrl: String!
        $userPoolId: String!
    ) {
        getActiveEntities(
            clientId: $clientId
            trqkDirectoryUrl: $trqkDirectoryUrl
            userPoolId: $userPoolId
        ) @client
    }
`;
